import {
  Dialog, DialogActions, DialogContent, Grid, Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import DatePicker from 'components/date-picker/date-picker';
import NumberField from 'components/number-field';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InterestKindEnum } from 'v2-types/order';

import { PercentField } from './components/percentField';
import { useCreateInterestModal } from './hooks';
import { useStyles } from './styles';

type Props = {
  onClose: () => void,
  orderType: string,
  loanId?: string
}

export const CreateInterestModal: FC<Props> = ({
  onClose,
  orderType,
  loanId
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    createInterestForm,
    onConfirm,
    isCreateInterestLoading,
    kindListing,
    isLoadingListing
  } = useCreateInterestModal({ onClose, orderType, loanId });

  const { control, reset } = createInterestForm;
  const { kind: kindSelected } = useWatch({ control });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogContent style={{ padding: 0 }}>
        <Grid container direction="column">
          <Grid item>
            <Typography variant="h5" className={classes.title}>
              {t('loan:createLoanInterest')}
            </Typography>
          </Grid>
          <Grid item container className={classes.container}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="kind"
                render={({ field }) => (
                  <SelectField
                    label={t('orders:kindLabel')}
                    value={field.value}
                    handleBlur={field.onBlur}
                    handleChange={(e) => {
                      reset({ kind: e.target.value as InterestKindEnum });
                    }}
                    data={kindListing}
                    dataKey="code"
                    dataValue="code"
                    dataText="description"
                    disabled={isLoadingListing}
                  />
                )}
              />
            </Grid>

            {(kindSelected === InterestKindEnum.only || kindSelected === InterestKindEnum.fixed || kindSelected === InterestKindEnum.variable) && (
              <Grid item xs={12}>
                <PercentField
                  control={control}
                  name="rate"
                  label={t('orders:rateLabelInterest')}
                />
              </Grid>
            )}

            {kindSelected === InterestKindEnum.adjustable && (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="index_name"
                    render={({ field }) => (
                      <TextField
                        color="secondary"
                        label={<Typography variant="body2">{t('orders:indexNameLabel')}</Typography>}
                        name="index_name"
                        handleBlur={field.onBlur}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="margin"
                    label={t('orders:marginLabel')}
                  />
                </Grid>
              </>
            )}

            {kindSelected === InterestKindEnum.conversion && (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="fee"
                    render={({ field }) => (
                      <NumberField
                        color="secondary"
                        label={t('orders:feeLabel')}
                        name="fee"
                        handleBlur={field.onBlur}
                        value={field.value}
                        onChange={(e) => {
                          let value = Number(e.target.value);
                          if (Number.isNaN(value)) {
                            value = 0;
                          }
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="period_start"
                    render={({ field }) => (
                      <DatePicker
                        label={t('orders:periodStartLabel')}
                        name="period-start"
                        error={false}
                        handleBlur={field.onBlur}
                        value={field.value || ''}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="period_end"
                    render={({ field }) => (
                      <DatePicker
                        label={t('orders:periodEndLabel')}
                        name="period-end"
                        error={false}
                        handleBlur={field.onBlur}
                        value={field.value || ''}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {kindSelected === InterestKindEnum.first && (
              <>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="change_date"
                    render={({ field }) => (
                      <DatePicker
                        label={t('orders:changeDateLabel')}
                        name="change date"
                        error={false}
                        handleBlur={field.onBlur}
                        value={field.value || ''}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="change_min"
                    label={t('orders:changeMinLabel')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="change_max"
                    label={t('orders:changeMaxLabel')}
                  />
                </Grid>
              </>
            )}

            {kindSelected === InterestKindEnum.subsequent && (
              <>
                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="change_max"
                    label={t('orders:changeMaxLabel')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="months"
                    render={({ field }) => (
                      <NumberField
                        color="secondary"
                        label={t('orders:monthsLabel')}
                        name="months"
                        handleBlur={field.onBlur}
                        value={field.value}
                        onChange={(e) => {
                          let value = Number(e.target.value);
                          if (Number.isNaN(value)) {
                            value = 0;
                          }
                          field.onChange(value);
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="rate_min"
                    label={t('orders:rateMinLabel')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <PercentField
                    control={control}
                    name="rate_max"
                    label={t('orders:rateMaxLabel')}
                  />
                </Grid>
              </>
            )}

          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton
          text={t('orders:closeButton')}
          onClick={onClose}
          disabled={isCreateInterestLoading}
        />
        <ContainedButton
          text={t('orders:createButton')}
          isLoading={isCreateInterestLoading || isLoadingListing}
          onClick={onConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};
